// 1. 코어 라이브러리 및 애플리케이션 설정 임포트
import { createApp, defineAsyncComponent } from 'vue'
import { createHead } from '@vueuse/head'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify3'
import registerPlugins from './plugins'
import pinia from './pinia'
import i18n from './lang/lang'
import { isChunkLikeError } from '@/utils/chunkError'

// 2. 전역 컴포넌트 임포트
import Tooltip from '@/components/dialog/BrickTooltip'
import StatusChip from '@/components/common/StatusChip.vue'
import DetailActionButton from '@/components/base/DetailActionButton.vue'
import DetailInfoCard from '@/components/base/DetailInfoCard.vue'
import DetailInfoGrid from '@/components/base/DetailInfoGrid.vue'
import ShowAllToggle from '@/components/base/ShowAllToggle.vue'
const VerticalWhiteSidebar = defineAsyncComponent(() =>
  import('@/layouts/section/Index')
)

// 3. 전역 스타일시트 임포트
import '@/assets/scss/app.scss' // 커스텀 SCSS
import '@mdi/font/css/materialdesignicons.css'

if (typeof globalThis === 'object' && !('global' in globalThis)) {
  globalThis.global = globalThis
}

const CHUNK_STARTUP_RELOAD_KEY = 'brickml:startup-chunk-reload'
const CHUNK_RUNTIME_RELOAD_KEY = 'brickml:runtime-chunk-reload'

const reloadOnce = (storageKey) => {
  if (typeof window === 'undefined') return false
  if (window.sessionStorage.getItem(storageKey)) {
    window.sessionStorage.removeItem(storageKey)
    return false
  }
  window.sessionStorage.setItem(storageKey, 'true')
  window.location.reload()
  return true
}

// 개발 환경에서는 청크 에러 시 전역 reload를 하지 않음.
// 라우터/컴포넌트의 retry·location.assign 폴백이 실행되어 목표 경로로 이동할 수 있게 함.
const isDev = import.meta.env.DEV

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLikeError(event?.reason)) return
    event.preventDefault()
    if (isDev) return
    reloadOnce(CHUNK_RUNTIME_RELOAD_KEY)
  })

  window.addEventListener('error', (event) => {
    const targetError = event?.error || event?.message
    if (!isChunkLikeError(targetError)) return
    if (isDev) return
    reloadOnce(CHUNK_RUNTIME_RELOAD_KEY)
  })
}

const app = createApp(App)
const head = createHead()

if (typeof globalThis === 'object') {
  const appConfig =
    globalThis.__APP_CONFIG__ && typeof globalThis.__APP_CONFIG__ === 'object'
      ? globalThis.__APP_CONFIG__
      : {}
  globalThis.__APP_CONFIG__ = {
    ...appConfig,
    VITE_FRONTEND_TYPE: import.meta.env.VITE_FRONTEND_TYPE,
  }
}

app.use(router)
app.use(pinia)
app.use(vuetify)
app.use(i18n)
app.use(head)

registerPlugins(app)

app.mixin({
  created() {
    const data = this.$data
    if (!data || typeof data !== 'object') return

    Object.keys(data).forEach((key) => {
      const value = data[key]
      if (!Array.isArray(value)) return

      value.forEach((item) => {
        if (!item || typeof item !== 'object') return
        if (!('text' in item) || !('value' in item)) return

        if (!('title' in item)) item.title = item.text
        if (!('key' in item)) item.key = item.value
      })
    })
  },
})

app.component('Tooltip', Tooltip)
app.component('VerticalWhiteSidebar', VerticalWhiteSidebar)
app.component('StatusChip', StatusChip)
app.component('DetailActionButton', DetailActionButton)
app.component('DetailInfoCard', DetailInfoCard)
app.component('DetailInfoGrid', DetailInfoGrid)
app.component('ShowAllToggle', ShowAllToggle)

router
  .isReady()
  .then(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(CHUNK_STARTUP_RELOAD_KEY)
      window.sessionStorage.removeItem(CHUNK_RUNTIME_RELOAD_KEY)
    }
    app.mount('#app')
  })
  .catch((error) => {
    console.error('[main] router startup failed', error)
    if (isChunkLikeError(error) && reloadOnce(CHUNK_STARTUP_RELOAD_KEY)) {
      return
    }
    app.mount('#app')
  })
